import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { reviewApi, menuApi } from '../../services/api';

const Stars = ({ rating = 0, size = 13, interactive = false, onRate }) => {
  const [hover, setHover] = useState(0);
  const display = interactive ? (hover || rating) : rating;
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(s => (
        <span key={s}
          onClick={() => interactive && onRate && onRate(s)}
          onMouseEnter={() => interactive && setHover(s)}
          onMouseLeave={() => interactive && setHover(0)}
          style={{ fontSize: size, color: s <= display ? '#f59e0b' : '#e8e0d8', lineHeight: 1, cursor: interactive ? 'pointer' : 'default', transition: 'color 0.1s' }}>★</span>
      ))}
    </div>
  );
};

// Edit Modal
const EditModal = ({ review, onClose, onSaved }) => {
  const [rating, setRating] = useState(review.rating);
  const [comment, setComment] = useState(review.comment || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!rating) return toast.error('Please select a rating');
    setSaving(true);
    try {
      await reviewApi.update(review.id, { rating, comment });
      toast.success('Review updated!');
      onSaved({ ...review, rating, comment });
      onClose();
    } catch {
      toast.error('Failed to update review');
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 14,
    border: '1.5px solid #e8d5c4', background: '#fffaf7',
    color: '#1a0f00', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'absolute', inset: 0, background: 'rgba(26,15,0,0.7)', backdropFilter: 'blur(4px)' }}
        onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.93, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.93 }}
        style={{ position: 'relative', background: '#fff', borderRadius: 22, width: '100%', maxWidth: 420, boxShadow: '0 32px 80px rgba(0,0,0,0.25)', border: '1px solid #f0e4d8', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f5ede4', background: 'linear-gradient(135deg,#fff8f3,#fff)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: 'linear-gradient(135deg,#c96428,#9a4a1e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>✏️</div>
            <div>
              <h2 style={{ fontFamily: '"Playfair Display",serif', fontSize: 17, fontWeight: 700, color: '#1a0f00', margin: 0 }}>Edit Review</h2>
              <p style={{ fontSize: 12, color: '#8b6347', margin: 0 }}>by {review.reviewer_name}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 10, border: '1px solid #e8d5c4', background: '#fff', cursor: 'pointer', fontSize: 15, color: '#8b6347', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Rating */}
          <div>
            <label style={{ display: 'block', color: '#6b4c35', fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Rating *</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Stars rating={rating} size={32} interactive onRate={setRating} />
              <span style={{ fontSize: 13, color: '#b08060', fontWeight: 600 }}>
                {['', 'Very Bad', 'Not Good', 'Okay', 'Good', 'Excellent!'][rating] || 'Select rating'}
              </span>
            </div>
          </div>

          {/* Comment */}
          <div>
            <label style={{ display: 'block', color: '#6b4c35', fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Comment (optional)</label>
            <textarea rows={4} value={comment} onChange={e => setComment(e.target.value)}
              placeholder="Customer's comment..."
              style={{ ...inp, resize: 'none', lineHeight: 1.6 }} />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={onClose}
              style={{ flex: 1, padding: '12px', borderRadius: 12, border: '1.5px solid #e8d5c4', background: '#fff', color: '#6b4c35', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ flex: 2, padding: '12px', borderRadius: 12, border: 'none', background: saving ? '#e8d5c4' : 'linear-gradient(135deg,#c96428,#9a4a1e)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', boxShadow: saving ? 'none' : '0 4px 16px rgba(201,100,40,0.35)' }}>
              {saving ? '⏳ Saving...' : '✅ Save Changes'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const AdminReviews = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [editReview, setEditReview] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reviewCounts, setReviewCounts] = useState({}); // { itemId: { total, average } }

  useEffect(() => {
    const fetchMenu = async () => {
      setLoadingMenu(true);
      try {
        const res = await menuApi.getAll({ limit: 200 });
        const items = res.data.data || [];
        setMenuItems(items);
        // Fetch review counts for all items in parallel
        const counts = {};
        await Promise.all(
          items.map(async (item) => {
            try {
              const r = await reviewApi.getAll(item.id);
              const s = r.data.data?.stats;
              counts[item.id] = { total: parseInt(s?.total || 0), average: parseFloat(s?.average || 0) };
            } catch {
              counts[item.id] = { total: 0, average: 0 };
            }
          })
        );
        setReviewCounts(counts);
      } catch {
        toast.error('Failed to load menu items');
      } finally {
        setLoadingMenu(false);
      }
    };
    fetchMenu();
  }, []);

  const loadReviews = async (item) => {
    setSelectedItem(item);
    setLoadingReviews(true);
    try {
      const res = await reviewApi.getAll(item.id);
      setReviews(res.data.data.reviews || []);
      setStats(res.data.data.stats || null);
    } catch {
      toast.error('Failed to load reviews');
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await reviewApi.delete(deleteConfirm);
      toast.success('Review deleted!');
      setDeleteConfirm(null);
      setReviews(prev => prev.filter(r => r.id !== deleteConfirm));
      if (stats) setStats(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      if (selectedItem) {
        setReviewCounts(prev => ({
          ...prev,
          [selectedItem.id]: { ...prev[selectedItem.id], total: Math.max(0, (prev[selectedItem.id]?.total || 1) - 1) }
        }));
      }
    } catch {
      toast.error('Failed to delete review');
    } finally {
      setDeleting(false);
    }
  };

  const handleEdited = (updated) => {
    setReviews(prev => prev.map(r => r.id === updated.id ? updated : r));
  };

  const ratingColor = (r) => r >= 4 ? '#22c55e' : r === 3 ? '#f59e0b' : '#ef4444';

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: '"Playfair Display",serif', fontSize: 24, fontWeight: 700, color: '#1a0f00', margin: 0 }}>Reviews Management</h1>
        <p style={{ fontSize: 13, color: '#8b6347', marginTop: 4 }}>View, edit, and delete customer reviews</p>
      </div>

      <div style={{ display: 'flex', gap: 24, height: 'calc(100vh - 180px)', minHeight: 500 }}>

        {/* LEFT: Menu Items */}
        <div style={{ width: 280, flexShrink: 0, background: '#fff', borderRadius: 18, border: '1px solid #f0e8df', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
          <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f5ede4', background: 'linear-gradient(135deg,#fff8f3,#fff)' }}>
            <h2 style={{ fontFamily: '"Playfair Display",serif', fontSize: 15, fontWeight: 700, color: '#1a0f00', margin: 0 }}>Menu Items</h2>
            <p style={{ fontSize: 11, color: '#8b6347', margin: '3px 0 0' }}>Select to view reviews</p>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
            {loadingMenu ? (
              Array(6).fill(0).map((_, i) => (
                <div key={i} style={{ height: 52, borderRadius: 12, background: '#f5ede4', marginBottom: 8 }} />
              ))
            ) : (
              menuItems.map(item => (
                <button key={item.id} onClick={() => loadReviews(item)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: 12, marginBottom: 6,
                    border: `1.5px solid ${selectedItem?.id === item.id ? '#c96428' : '#f0e8df'}`,
                    background: selectedItem?.id === item.id ? '#fff4ee' : '#fff',
                    cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
                    boxShadow: selectedItem?.id === item.id ? '0 2px 10px rgba(201,100,40,0.15)' : 'none',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a0f00', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{item.name}</div>
                    {reviewCounts[item.id]?.total > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0, background: '#fff4ee', border: '1px solid #f0d0b0', borderRadius: 99, padding: '2px 7px' }}>
                        <span style={{ fontSize: 10, color: '#f59e0b' }}>★</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#c96428' }}>{reviewCounts[item.id].total}</span>
                      </div>
                    ) : (
                      <div style={{ flexShrink: 0, background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 99, padding: '2px 7px' }}>
                        <span style={{ fontSize: 10, color: '#bbb', fontWeight: 600 }}>0</span>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#c96428', marginTop: 2 }}>{item.category_name || 'Uncategorized'}</div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Reviews */}
        <div style={{ flex: 1, background: '#fff', borderRadius: 18, border: '1px solid #f0e8df', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
          {!selectedItem ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#b08060' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>⭐</div>
              <p style={{ fontSize: 16, fontWeight: 600, fontFamily: '"Playfair Display",serif', color: '#6b4c35', margin: 0 }}>Select a menu item</p>
              <p style={{ fontSize: 13, color: '#b08060', marginTop: 6 }}>to view and manage its reviews</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding: '16px 24px 12px', borderBottom: '1px solid #f5ede4', background: 'linear-gradient(135deg,#fff8f3,#fff)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h2 style={{ fontFamily: '"Playfair Display",serif', fontSize: 17, fontWeight: 700, color: '#1a0f00', margin: 0 }}>{selectedItem.name}</h2>
                  <p style={{ fontSize: 12, color: '#8b6347', margin: '3px 0 0' }}>
                    {loadingReviews ? 'Loading...' : `${stats?.total || 0} review${stats?.total !== 1 ? 's' : ''}${stats?.total > 0 ? ` · Avg ${stats.average} ★` : ''}`}
                  </p>
                </div>
                {stats?.total > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#c96428', fontFamily: '"Playfair Display",serif', lineHeight: 1 }}>{stats.average}</div>
                    <Stars rating={parseFloat(stats.average)} size={13} />
                  </div>
                )}
              </div>

              {/* List */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 24px' }}>
                {loadingReviews ? (
                  Array(4).fill(0).map((_, i) => (
                    <div key={i} style={{ height: 80, borderRadius: 14, background: '#f5ede4', marginBottom: 12 }} />
                  ))
                ) : reviews.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 0', color: '#b08060' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#6b4c35', margin: 0 }}>No reviews yet</p>
                  </div>
                ) : (
                  <AnimatePresence>
                    {reviews.map(review => (
                      <motion.div key={review.id}
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }}
                        style={{ padding: '14px 18px', background: '#fffaf7', borderRadius: 14, border: '1px solid #f0e8df', marginBottom: 10, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#1a0f00' }}>{review.reviewer_name}</span>
                            <span style={{ fontSize: 12, fontWeight: 800, color: ratingColor(review.rating), background: `${ratingColor(review.rating)}15`, padding: '2px 8px', borderRadius: 99, border: `1px solid ${ratingColor(review.rating)}30` }}>
                              {review.rating} ★
                            </span>
                            <Stars rating={review.rating} size={11} />
                          </div>
                          {review.comment && (
                            <p style={{ fontSize: 13, color: '#6b4c35', margin: '0 0 6px', lineHeight: 1.6 }}>{review.comment}</p>
                          )}
                          <span style={{ fontSize: 11, color: '#b08060' }}>
                            {new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>

                        {/* Edit + Delete buttons */}
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                          <button onClick={() => setEditReview(review)}
                            style={{ width: 34, height: 34, borderRadius: 10, border: '1.5px solid #c96428', background: '#fff', color: '#c96428', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
                            onMouseOver={e => { e.currentTarget.style.background = '#c96428'; e.currentTarget.style.color = '#fff'; }}
                            onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#c96428'; }}
                            title="Edit review">✏️</button>
                          <button onClick={() => setDeleteConfirm(review.id)}
                            style={{ width: 34, height: 34, borderRadius: 10, border: '1.5px solid #fee2e2', background: '#fff5f5', color: '#ef4444', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
                            onMouseOver={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
                            onMouseOut={e => { e.currentTarget.style.background = '#fff5f5'; e.currentTarget.style.color = '#ef4444'; }}
                            title="Delete review">🗑️</button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editReview && (
          <EditModal
            review={editReview}
            onClose={() => setEditReview(null)}
            onSaved={handleEdited}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'absolute', inset: 0, background: 'rgba(26,15,0,0.7)', backdropFilter: 'blur(4px)' }}
              onClick={() => setDeleteConfirm(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              style={{ position: 'relative', background: '#1a0f00', border: '1px solid #3d2010', borderRadius: 22, padding: 32, width: '100%', maxWidth: 360, textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.4)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🗑️</div>
              <h3 style={{ fontFamily: '"Playfair Display",serif', fontSize: 18, fontWeight: 700, color: '#fde0cc', margin: '0 0 8px' }}>Delete Review?</h3>
              <p style={{ fontSize: 13, color: '#8b6347', marginBottom: 24 }}>This action cannot be undone.</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setDeleteConfirm(null)}
                  style={{ flex: 1, padding: '11px', borderRadius: 12, border: '1px solid #3d2010', background: 'transparent', color: '#a07858', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Cancel
                </button>
                <button onClick={handleDelete} disabled={deleting}
                  style={{ flex: 1, padding: '11px', borderRadius: 12, border: 'none', background: deleting ? '#888' : '#ef4444', color: '#fff', fontSize: 14, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminReviews;
